/* eslint-disable security/detect-object-injection */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useTrail, animated, useSpring, config } from 'react-spring';
import VisibilitySensor from 'react-visibility-sensor';
import { InteractiveList } from '../../components/InteractiveList';
import { AnimatedHeader } from '../../components/AnimatedHeader';
import { useScrollToElement } from '../../hooks/useScrollToElement';
import { Footer } from '../../components/Footer';
import { SplitWords } from '../../functions/split';

interface Props extends RouteComponentProps<{ id: string }> {}

const Gdpr: React.FC<Props> = () => {
    const { t } = useTranslation();
    const [smoothScrollbar, setSmoothScrollbar] = useState(null);
    const [navFixed, setNavFixed] = useState(false);
    const { scrollTo } = useScrollToElement(smoothScrollbar);
    const hasWindow = typeof window !== 'undefined' ? true : false;
    let scrollbar: any = null;
    let smoothScroll: any = null;

    const NAVIGATION_LINKS = [
        { name: 'general', tr: t('gdpr.nav.general') },
        { name: 'who', tr: t('gdpr.nav.who') },
        { name: 'why', tr: t('gdpr.nav.why') },
        { name: 'protection', tr: t('gdpr.nav.protection') },
        { name: 'where', tr: t('gdpr.nav.where') },
        { name: 'rights', tr: t('gdpr.nav.rights') },
        { name: 'correction', tr: t('gdpr.nav.correction') },
        { name: 'deletion', tr: t('gdpr.nav.deletion') },
        { name: 'limitation', tr: t('gdpr.nav.limitation') },
        { name: 'objection', tr: t('gdpr.nav.objection') },
        { name: 'contact', tr: t('gdpr.nav.contact') },
    ];

    const GDPR_CONTENT = [
        { name: 'general', tr: t('gdpr.content.general') },
        { name: 'who', tr: t('gdpr.content.who') },
        { name: 'why', tr: t('gdpr.content.why') },
        { name: 'protection', tr: t('gdpr.content.protection') },
        { name: 'where', tr: t('gdpr.content.where') },
        { name: 'rights', tr: t('gdpr.content.rights') },
        { name: 'correction', tr: t('gdpr.content.correction') },
        { name: 'deletion', tr: t('gdpr.content.deletion') },
        { name: 'limitation', tr: t('gdpr.content.limitation') },
        { name: 'objection', tr: t('gdpr.content.objection') },
        { name: 'contact', tr: t('gdpr.content.contact') },
    ];

    const navTrail = useTrail(NAVIGATION_LINKS.length, {
        delay: 500,
        opacity: 1,
        x: 0,
        from: { opacity: 0, x: -20 },
    });

    const headerFade = useSpring({
        delay: 500,
        opacity: 1,
        from: { opacity: 0 },
        config: config.molasses,
    });

    useEffect(() => {
        if (hasWindow) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            scrollbar = require('smooth-scrollbar');
            console.log('scroll', scrollbar);
            const plugin = require('../../functions/edgeScrollEasingPlugin');
            scrollbar.default.use(plugin.default);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            smoothScroll = scrollbar.default.init(document.querySelector('#am_scrollbar'), {
                thumbMinSize: 20,
                renderByPixels: true,
                continuousScrolling: false,
            });
            setSmoothScrollbar(smoothScroll);
            smoothScroll.addListener((s: any) => {
                console.log(s.offset);
                console.log(smoothScroll._momentum);
                const gdprText = document.querySelector('.am_gdpr_text');
                //console.log(gdprText!.getBoundingClientRect());

                // eslint-disable-next-line no-unused-expressions
                gdprText!.getBoundingClientRect().y <= 310 ? setNavFixed(true) : setNavFixed(false);
            });
        }

        return () => {
            scrollbar && smoothScroll && smoothScroll.destroy();
        };
    }, [hasWindow]);

    //useEventListener('scroll', scrollTrigger);
    //

    return (
        <>
            <Helmet defaultTitle="AutoMat | Användarvillkor & GDPR" titleTemplate="%s AutoMat" />
            <div
                className="am_gdpr_navigation"
                style={{ position: navFixed ? 'fixed' : 'absolute' }}
            >
                <ul
                    onClick={(e: React.MouseEvent<HTMLUListElement, MouseEvent> | any) =>
                        e.target.className && scrollTo(document.getElementById(e.target.className))
                    }
                >
                    {navTrail.map(({ x, opacity, ...rest }, index) => (
                        <animated.div
                            key={index}
                            style={{
                                ...rest,
                                transform: x.interpolate((x) => `translate3d(${x}px,0,0)`),
                                opacity: opacity,
                            }}
                            className={`am_gdpr_container ${NAVIGATION_LINKS[index].name}`}
                        >
                            <InteractiveList
                                name={NAVIGATION_LINKS[index].name}
                                text={NAVIGATION_LINKS[index].tr}
                                col={['#ffffff', '#acc9be']}
                            />
                        </animated.div>
                    ))}
                </ul>
            </div>
            <div id="am_scrollbar">
                <div id="am_route_content" className="gdpr">
                    <animated.div className="am_gdpr_header" style={headerFade}>
                        <AnimatedHeader message={t('gdpr.message')} len={-1790} dur={20} />
                    </animated.div>
                    <div className="am_gdpr_content">
                        {GDPR_CONTENT.map((content, index) => (
                            <div key={index} id={content.name} className="am_gdpr_text">
                                <VisibilitySensor partialVisibility>
                                    <SplitWords>
                                        <p>{content.tr}</p>
                                    </SplitWords>
                                </VisibilitySensor>
                            </div>
                        ))}
                    </div>
                    <Footer />
                </div>
            </div>
        </>
    );
};

export default Gdpr;
